import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, put, del } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useReconnect } from "@/hooks/use-reconnect";
import type { Document, DocumentType, Folder, SermonMetadata } from "@/types";

/**
 * One source of truth for the library — documents, folders, types, upcoming
 * sermons — behind TanStack Query.
 *
 * The dashboard, the sidebar, the folder tree and the document list each used
 * to fetch `/documents` independently on the same screen load, three of them
 * unfiltered. Beyond the wasted round trips they disagreed: archiving from the
 * list left the sidebar showing the document until a reload. Sharing a query
 * key fixes both at once, so keep new callers on these hooks rather than
 * reaching for `get()` directly.
 */

export type FolderSelection = number | "all" | "unfiled";

export interface DocumentFilters {
  folder?: FolderSelection;
  docType?: string | null;
  tagId?: number | null;
  archived?: boolean;
}

export interface UpcomingSermon extends SermonMetadata {
  document: Document | null;
}

/** Query-string for the API and cache key for TanStack, derived together so they cannot drift. */
function documentsQuery(filters: DocumentFilters): string {
  const params = new URLSearchParams();
  if (filters.folder === "unfiled") params.set("folder", "unfiled");
  else if (typeof filters.folder === "number") params.set("folder", String(filters.folder));
  if (filters.docType) params.set("type", filters.docType);
  if (filters.tagId) params.set("tag", String(filters.tagId));
  if (filters.archived) params.set("archived", "true");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Global default is `staleTime: Infinity`, so nothing refetches on its own.
 * That is right for writing — but after an outage every list would sit empty
 * until a hard reload. Invalidating on the reconnect epoch keeps the old
 * `useReconnect()` behaviour without each screen re-implementing it.
 */
function useReconnectInvalidation() {
  const reconnectKey = useReconnect();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (reconnectKey === 0) return;
    void queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/folders"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/sermons"] });
  }, [reconnectKey, queryClient]);
}

export function useDocuments(filters: DocumentFilters = {}) {
  useReconnectInvalidation();
  const qs = documentsQuery(filters);
  return useQuery({
    queryKey: ["/api/documents", qs],
    queryFn: () => get<Document[]>(`/documents${qs}`),
  });
}

export function useFolders() {
  useReconnectInvalidation();
  return useQuery({
    queryKey: ["/api/folders"],
    queryFn: () => get<Folder[]>("/folders"),
  });
}

export function useDocumentTypes() {
  return useQuery({
    queryKey: ["/api/document-types"],
    queryFn: () => get<DocumentType[]>("/document-types"),
  });
}

/** Sermons with a delivery date still ahead of us, soonest first. */
export function useUpcomingSermons(limit = 4) {
  useReconnectInvalidation();
  return useQuery({
    queryKey: ["/api/sermons", "upcoming", limit],
    queryFn: () => get<UpcomingSermon[]>(`/sermons?upcoming=true&limit=${limit}`),
  });
}

/** Invalidate everything a document write can affect. Folder counts move too. */
export function useInvalidateLibrary() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/folders"] });
    void queryClient.invalidateQueries({ queryKey: ["/api/sermons"] });
  };
}

/**
 * Archive is a soft delete, so it is reversible — and a destructive-looking
 * action that can be undone should not stop the user with a `confirm()`.
 * Archive immediately, then offer the way back in the toast.
 */
export function useArchiveDocument() {
  const { toast } = useToast();
  const invalidate = useInvalidateLibrary();

  const restore = useMutation({
    mutationFn: (id: number) => put(`/documents/${id}`, { is_archived: false }),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: "Couldn't restore that document", variant: "destructive" }),
  });

  return useMutation({
    mutationFn: (document: Document) => del(`/documents/${document.id}`),
    onSuccess: (_result, document) => {
      invalidate();
      toast({
        title: "Document archived",
        description: document.title || "Untitled",
        action: (
          <ToastAction altText="Undo" onClick={() => restore.mutate(document.id)}>
            Undo
          </ToastAction>
        ),
      });
    },
    onError: () =>
      toast({ title: "Couldn't archive that document", variant: "destructive" }),
  });
}

export function useRestoreDocument() {
  const { toast } = useToast();
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: (id: number) => put(`/documents/${id}`, { is_archived: false }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Document restored" });
    },
    onError: () =>
      toast({ title: "Couldn't restore that document", variant: "destructive" }),
  });
}

export function useMoveDocument() {
  const { toast } = useToast();
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: number; folderId: number | null }) =>
      put(`/documents/${id}`, { folder_id: folderId }),
    onSuccess: invalidate,
    onError: () => toast({ title: "Couldn't move that document", variant: "destructive" }),
  });
}

export function useDeleteFolder() {
  const { toast } = useToast();
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: (folder: Folder) => del(`/folders/${folder.id}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Folder deleted", description: "Its documents moved to No folder." });
    },
    onError: () => toast({ title: "Couldn't delete that folder", variant: "destructive" }),
  });
}

export interface FolderNode extends Folder {
  children: FolderNode[];
}

/** Flat folder rows → the nested shape both the sidebar and the filter bar render. */
export function buildFolderTree(folders: Folder[]): FolderNode[] {
  const build = (parentId: number | null): FolderNode[] =>
    folders
      .filter((folder) => (folder.parent_id ?? null) === parentId)
      .map((folder) => ({ ...folder, children: build(folder.id) }));
  return build(null);
}
